import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';

const ConnectivityContent = () => {
    const [activePortIndex, setActivePortIndex] = useState(0);

    const portData = [
        {
            name: "Thunderbolt 4 (Upstream)",
            title: "Pro-grade bandwidth.",
            description: "One port for everything. Data transfer up to 40Gb/s, video output, and power delivery in a single universal connector."
        },
        {
            name: "3x USB-C (10Gb/s)",
            title: "Versatile expansion.",
            description: "Connect high-speed storage, cameras, and sensors with dedicated 10Gb/s bandwidth for seamless data ingestion."
        },
        {
            name: "HDMI 2.1 (8K)",
            title: "Drive external displays.",
            description: "Connect up to two high-resolution 8K external displays for immersive projection mapping and digital signage."
        },
        {
            name: "10Gb Ethernet",
            title: "Hyper-fast networking.",
            description: "Low-latency, high-bandwidth wired networking for large-scale installations and synchronized multi-ARK clusters."
        },
        {
            name: "Audio Out",
            title: "High-fidelity audio.",
            description: "3.5mm analog output with support for high-impedance headphones and external sound systems."
        }
    ];

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-start">
             {/* Left: Port List (Apple Style) */}
             <div className="flex flex-col gap-4 md:gap-2">
                {portData.map((item, i) => (
                    <motion.div
                        key={i}
                        onMouseEnter={() => setActivePortIndex(i)}
                        onClick={() => setActivePortIndex(i)}
                        className="cursor-pointer group"
                    >
                        <motion.h3 
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.1, duration: 0.5 }}
                            className={`text-2xl md:text-5xl font-bold transition-all duration-300 tracking-tighter ${activePortIndex === i ? 'text-white' : 'text-zinc-500 group-hover:text-zinc-300'}`}
                        >
                            {item.name}
                        </motion.h3>
                        
                        {/* Mobile-only inline description */}
                        <div className="md:hidden">
                           <AnimatePresence>
                               {activePortIndex === i && (
                                   <motion.div
                                       initial={{ opacity: 0, height: 0 }}
                                       animate={{ opacity: 1, height: 'auto' }}
                                       exit={{ opacity: 0, height: 0 }}
                                       transition={{ duration: 0.3 }}
                                       className="overflow-hidden"
                                   >
                                       <p className="text-base text-zinc-400 mt-2 leading-relaxed">
                                           <span className="text-white font-medium">{item.title}</span> {item.description}
                                       </p>
                                   </motion.div>
                               )}
                           </AnimatePresence>
                        </div>
                    </motion.div>
                ))}
             </div>

             {/* Right: Description / Highlight - Hidden on mobile, shown on desktop */}
             <div className="flex flex-col justify-between h-full">
                <div className="hidden md:block min-h-[200px]">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activePortIndex}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.3 }}
                        >
                            <p className="text-lg md:text-2xl font-medium text-white leading-tight mb-4">
                                {portData[activePortIndex].title} <span className="text-zinc-500">{portData[activePortIndex].description}</span>
                            </p>
                        </motion.div>
                    </AnimatePresence>
                </div>
                
                <div className="mt-8 md:mt-0">
                    <div className="h-px w-full bg-white/10 mb-8" />
                    <div className="grid grid-cols-2 gap-8">
                        <div>
                            <h4 className="text-white font-bold text-lg mb-1">Wi-Fi 6E</h4>
                            <p className="text-zinc-400 text-sm">Ultra-fast wireless syncing.</p>
                        </div>
                        <div>
                            <h4 className="text-white font-bold text-lg mb-1">Bluetooth 5.3</h4>
                            <p className="text-zinc-400 text-sm">Low-latency controller support.</p>
                        </div>
                    </div>
                </div>
             </div>
        </div>
    );
};

export default ConnectivityContent;
